import { createElement, useCallback, useMemo, useState } from 'react';
import type { HoaDonMuaDichVu } from '@/types/schemas/hoa-don-mua-dich-vu.type';
import { getDataTableColumns } from '../cols-definition';
import { SearchFormValues } from '../schema';
import { useCRUD, useForms } from '@/hooks';
import { useSearchTable } from '../hooks';
import { QUERY_KEYS } from '@/constants';

export const useHoaDonMuaDichVuTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>({});

  const {
    data,
    detail,
    fetchDetail,
    addItem,
    deleteItem,
    refreshData,
    isLoading,
    handlePageChange,
    totalItems,
    currentPage,
    updateItem,
    searchData,
    isFiltered,
    sumTotal
  } = useCRUD<HoaDonMuaDichVu, MuaHangRecord>({
    endpoint: QUERY_KEYS.HOA_DON_MUA_DICH_VU,
    pageSize: 20,
    sumField: 't_tien',
    useFilter: true
  });

  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleRowClick,
    clearSelection,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const {
    handleSearchChange,
    onPageChange,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering,
    searchState,
    hasFilterParams
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    isFiltered,
    handlePageChange
  });

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      await handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [handleInitialSearch]
  );

  const handleRefresh = useCallback(async () => {
    await handleRefreshClick();
    await fetchDetail(selectedObj?.uuid);
  }, [fetchDetail, handleRefreshClick, selectedObj?.uuid]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const displayIsLoading = isLoading || isFiltering;
  const columns = useMemo(
    () => getDataTableColumns(handleViewClick, sumTotal, displayIsLoading),
    [displayIsLoading, handleViewClick, sumTotal]
  );

  const tables = useMemo(
    () => [
      {
        name: 'Tất cả',
        rows: displayData,
        columns
      },
      {
        name: 'Lập chứng từ',
        rows: displayData.filter(row => row.status === '0'),
        columns,
        icon: createElement('div', { className: 'mr-2 size-[7px] rounded-full bg-[#EAD1DC]' })
      },
      {
        name: 'Chờ duyệt',
        rows: displayData.filter(row => row.status === '3'),
        columns,
        icon: createElement('div', { className: 'mr-2 size-[7px] rounded-full bg-red-500' })
      },
      {
        name: 'Xóa hóa đơn',
        rows: displayData.filter(row => row.status === '5'),
        columns,
        icon: createElement('div', { className: 'mr-2 size-[7px] rounded-full bg-[#3D85C6]' })
      },
      {
        name: 'Khác',
        rows: displayData.filter(row => row.status === '99'),
        columns,
        icon: createElement('div', { className: 'mr-2 size-[7px] rounded-full bg-black' })
      }
    ],
    [columns, displayData]
  );

  return {
    crud: {
      data,
      detail,
      fetchDetail,
      addItem,
      deleteItem,
      refreshData,
      updateItem,
      searchData
    },
    selection: {
      selectedObj,
      selectedRowIndex,
      clearSelection
    },
    dialogs: {
      showForm,
      showDelete,
      showBulkDelete,
      isCopyMode
    },
    form: {
      formMode,
      handleRowClick,
      handleCloseBulkDelete,
      handleCloseForm,
      handleCloseDelete,
      handleAddClick,
      handleEditClick,
      handleViewClick,
      handleDeleteClick,
      handleCopyClick
    },
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading,
      currentPage: hasFilterParams ? searchState.currentPage : currentPage,
      totalItems: hasFilterParams ? searchState.totalCount : totalItems,
      onPageChange,
      onSearchChange: handleSearchChange,
      onRefresh: handleRefresh
    }
  };
};
